import React, { useState } from 'react';
import { List, ListItem, ListItemText, IconButton, Tooltip, Typography, Alert, Chip, Box } from '@mui/material';
import { Cancel } from '@mui/icons-material';
import type { RolloutDocument } from '@finvari/deliver';
import { useConfig } from '../contexts/ConfigContext';
import { usePools } from '../contexts/PoolsContext';
import { cancelQueuedRollout } from '../firebase';
import { formatDate } from '../utils/deploymentUtils';

interface QueuedDeploysListProps {
  rollouts: RolloutDocument[];
  // Show which pool each entry targets (useful on PR/main pages spanning pools).
  showPool?: boolean;
  emptyText?: string;
}

// Lists queued (not-yet-assigned) pool deploys with a Cancel action for the queuer or a config editor.
export default function QueuedDeploysList({ rollouts, showPool, emptyText }: QueuedDeploysListProps) {
  const { userProfile } = useConfig();
  const { poolsById } = usePools();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canCancel = (r: RolloutDocument) =>
    r.createdBy === userProfile?.uid || !!userProfile?.canEditConfig;

  const handleCancel = async (rolloutId: string) => {
    setBusyId(rolloutId);
    setError(null);
    try {
      await cancelQueuedRollout(rolloutId);
    } catch (e: any) {
      setError(e?.message || 'Failed to cancel queued deploy');
    } finally {
      setBusyId(null);
    }
  };

  if (rollouts.length === 0) {
    return emptyText ? (
      <Typography variant="body2" color="text.secondary">
        {emptyText}
      </Typography>
    ) : null;
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      <List dense disablePadding>
        {rollouts.map((r) => (
          <ListItem
            key={r.rolloutId}
            secondaryAction={
              canCancel(r) ? (
                <Tooltip title="Cancel queued deploy">
                  <span>
                    <IconButton edge="end" size="small" disabled={busyId === r.rolloutId} onClick={() => handleCancel(r.rolloutId)}>
                      <Cancel fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              ) : undefined
            }
          >
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <span>
                    {r.prNumber ? `PR #${r.prNumber}` : 'Main branch'}
                    {r.prTitle ? ` — ${r.prTitle}` : ''}
                  </span>
                  {showPool && r.poolId && (
                    <Chip size="small" variant="outlined" label={poolsById[r.poolId]?.name ?? r.poolId} />
                  )}
                </Box>
              }
              secondary={`Queued by ${r.createdBy} • ${formatDate(r.createdAt)}`}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );
}
